import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/layout/AdminLayoutNew';
import { Building2, Home, CheckCircle, Clock, XCircle, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, projectsRes] = await Promise.all([
          axios.get(`${API_URL}/api/dashboard/stats`, { headers: getAuthHeaders() }),
          axios.get(`${API_URL}/api/projects`, { headers: getAuthHeaders() })
        ]);
        setStats(statsRes.data);
        setProjects(projectsRes.data.filter(p => p.id === 'bakrajo-hills'));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getAuthHeaders]);

  const statCards = stats ? [
    { title: 'Total Projects', value: stats.total_projects, icon: Building2, color: 'text-white' },
    { title: 'Total Units', value: stats.total_units, icon: Home, color: 'text-white' },
    { title: 'Available', value: stats.available_units, icon: CheckCircle, color: 'text-green-500' },
    { title: 'Pending', value: stats.pending_units, icon: Clock, color: 'text-orange-500' },
    { title: 'Sold', value: stats.sold_units, icon: XCircle, color: 'text-red-500' },
  ] : [];

  return (
    <AdminLayout>
      <div className="space-y-10" data-testid="admin-dashboard">
        {/* Header */}
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2 font-light">Overview</p>
          <h1 className="text-3xl lg:text-4xl font-bold text-white tracking-tight">Dashboard</h1>
        </div>

        {/* Stats Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-32 bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-white/10">
            {statCards.map((stat, index) => (
              <div key={stat.title} className="bg-black p-6 lg:p-8">
                <stat.icon className={`w-6 h-6 ${stat.color} mb-4 opacity-60`} />
                <p className={`text-3xl lg:text-4xl font-light ${stat.color} mb-2`}>{stat.value}</p>
                <p className="text-[10px] uppercase tracking-[0.15em] text-white/40 font-light">{stat.title}</p>
              </div>
            ))}
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link 
            to="/admin/projects"
            className="group flex items-center justify-between p-6 lg:p-8 border border-white/10 hover:border-white/30 transition-colors"
            data-testid="quick-action-projects"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center border border-white/20">
                <Building2 className="w-5 h-5 text-white/60" />
              </div>
              <div>
                <h3 className="text-white font-medium">Manage Projects</h3>
                <p className="text-white/40 text-xs font-light">Add, edit, or remove projects</p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-white/30 group-hover:text-white transition-colors" />
          </Link>

          <Link 
            to="/admin/map-editor"
            className="group flex items-center justify-between p-6 lg:p-8 border border-white/10 hover:border-white/30 transition-colors"
            data-testid="quick-action-map-editor"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center border border-green-500/30 bg-green-500/10">
                <Home className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <h3 className="text-white font-medium">Map Editor</h3>
                <p className="text-white/40 text-xs font-light">Add & position unit markers</p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-white/30 group-hover:text-white transition-colors" />
          </Link>
        </div>

        {/* Recent Projects */}
        <div className="border border-white/10">
          <div className="px-6 py-4 border-b border-white/10">
            <h2 className="text-xs uppercase tracking-[0.2em] text-white/60 font-light">Recent Projects</h2>
          </div>
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-white/5 animate-pulse" />
              ))}
            </div>
          ) : projects.length > 0 ? (
            <div className="divide-y divide-white/10">
              {projects.slice(0, 5).map((project) => (
                <Link
                  key={project.id}
                  to={`/admin/map-editor/${project.id}`}
                  className="flex items-center gap-4 p-4 lg:p-6 hover:bg-white/5 transition-colors"
                  data-testid={`recent-project-${project.id}`}
                >
                  <div className="w-12 h-12 bg-white/5 overflow-hidden">
                    <img
                      src={project.thumbnail || 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=100&q=80'}
                      alt={project.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white font-medium truncate">{project.name}</h4>
                    <p className="text-white/40 text-xs font-light truncate">{project.location}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-light">{project.total_units || 0}</p>
                    <p className="text-white/40 text-[10px] uppercase tracking-[0.1em] font-light">Units</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-white/20" />
                </Link>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <p className="text-white/40 font-light">No projects yet</p>
              <Link to="/admin/projects" className="text-white text-xs uppercase tracking-[0.15em] hover:text-white/80 mt-4 inline-block">
                Add Project
              </Link>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
